import { modifier } from 'ember-modifier';

interface StickySidebarAdjustmentOptions {
  headerSelector?: string;
}

interface StickySidebarAdjustment {
  Args?: {
    Named?: StickySidebarAdjustmentOptions;
    Positional?: undefined;
  };
  Element: HTMLElement;
}

let resizeObserver: ResizeObserver | undefined;
let mutationObserver: MutationObserver | undefined;

function adjustSidebarPosition(sidebarElement: HTMLElement, headerSelector: string) {
  const headerElement = document.querySelector(headerSelector) as HTMLElement;

  if (!headerElement) {
    // No header found, stick to top
    sidebarElement.style.top = '0px';
    return;
  }

  const headerRect = headerElement.getBoundingClientRect();
  const headerHeight = headerRect.height;

  // Set the sidebar to stick at the bottom of the header
  sidebarElement.style.top = `${headerHeight}px`;
  sidebarElement.style.height = `calc(100vh - ${headerHeight}px)`;
}

function setupObservers(sidebarElement: HTMLElement, headerSelector: string) {
  // Watch for header size changes
  const headerElement = document.querySelector(headerSelector) as HTMLElement;

  if (headerElement && 'ResizeObserver' in window) {
    resizeObserver = new ResizeObserver(() => {
      adjustSidebarPosition(sidebarElement, headerSelector);
    });
    resizeObserver.observe(headerElement);
  }

  // Watch for DOM changes that might affect header
  if ('MutationObserver' in window) {
    mutationObserver = new MutationObserver(() => {
      // Debounce the adjustment to avoid excessive calculations
      setTimeout(() => {
        adjustSidebarPosition(sidebarElement, headerSelector);
      }, 16); // ~60fps
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style'],
    });
  }

  // Also listen to window resize
  const handleResize = () => {
    adjustSidebarPosition(sidebarElement, headerSelector);
  };

  window.addEventListener('resize', handleResize);

  return handleResize;
}

function cleanup(handleResize?: () => void) {
  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = undefined;
  }

  if (mutationObserver) {
    mutationObserver.disconnect();
    mutationObserver = undefined;
  }

  if (handleResize) {
    window.removeEventListener('resize', handleResize);
  }
}

export default modifier<StickySidebarAdjustment>((element, _positional, named) => {
  const options = { headerSelector: '[data-sticky-header]', ...named };

  // Find the sidebar within the layout element
  const sidebarElement = element.querySelector('[data-sticky-sidebar]') as HTMLElement;

  if (!sidebarElement) {
    console.warn('StickySidebarAdjustment: No sidebar element found with [data-sticky-sidebar]');
    return;
  }

  adjustSidebarPosition(sidebarElement, options.headerSelector);
  const handleResize = setupObservers(sidebarElement, options.headerSelector);

  // Return cleanup function
  return () => {
    cleanup(handleResize);
  };
});
